import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Upload, Package } from 'lucide-react';
import { useB2BProducts } from '@/hooks/use-b2b-products';
import B2BProductUpload from '@/components/b2b-products/upload/B2BProductUpload';
import B2BProductTable from '@/components/b2b-products/B2BProductTable';

const B2BProducts: React.FC = () => {
  const [showUpload, setShowUpload] = useState(false);
  const { data: products, isLoading, refetch } = useB2BProducts();

  const handleUploadSuccess = () => {
    setShowUpload(false);
    refetch();
  };

  const handleUploadCancel = () => {
    setShowUpload(false);
  };

  if (showUpload) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-6">
          <B2BProductUpload onSuccess={handleUploadSuccess} onCancel={handleUploadCancel} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">B2B Products</h1>
            <p className="text-muted-foreground">
              Manage B2B product pricing and inventory data
            </p>
          </div>
          <Button onClick={() => setShowUpload(true)} className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Upload Products
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Products</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? '...' : products?.length || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Active B2B products
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Average Price</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? '...' : products && products.length > 0 
                  ? `$${(products.reduce((sum, p) => sum + Number(p.price), 0) / products.length).toFixed(2)}`
                  : '$0.00'
                }
              </div>
              <p className="text-xs text-muted-foreground">
                Per unit average
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Price Range</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? '...' : products && products.length > 0 
                  ? `$${Math.min(...products.map(p => Number(p.price))).toFixed(2)} - $${Math.max(...products.map(p => Number(p.price))).toFixed(2)}`
                  : '$0.00'
                }
              </div>
              <p className="text-xs text-muted-foreground">
                Min - Max pricing
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>B2B Products</CardTitle>
            <CardDescription>
              View and manage your B2B product catalog with pricing information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <B2BProductTable products={products || []} isLoading={isLoading} />
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default B2BProducts;